import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDirtyValues<T extends Record<string, any>>(dirtyFields: any, values: T): Partial<T> {
  return Object.keys(dirtyFields).reduce((acc, key) => {
    if (dirtyFields[key] === true) {
      acc[key as keyof T] = values[key as keyof T];
    } else if (typeof dirtyFields[key] === "object" && dirtyFields[key] !== null) {
      acc[key as keyof T] = getDirtyValues(dirtyFields[key], values[key as keyof T]) as any; // Use 'as any' to bypass type error
    }
    return acc;
  }, {} as Partial<T>);
}

export interface DjangoError {
  type: string;
  messages: string[];
}

export function getDjangoError(errorResponse: any): DjangoError[] {
  if (!errorResponse?.response?.data) return [];

  return Object.entries(errorResponse.response.data).map(([type, messages]) => ({
    type,
    messages: Array.isArray(messages) ? messages : [messages],
  }));
}

export function getDjangoErrorMessages(errorResponse: any): string[] {
  return getDjangoError(errorResponse).flatMap((error) => error.messages);
}

export function getOffsetMarkers(properties: API.Property.PropertyMapData[], offsetDistance = 0.0001) {
  // Group by lat/lng rounded to 6 decimals
  const groups: Record<string, API.Property.PropertyMapData[]> = {};
  properties.forEach((prop) => {
    if (!prop.latitude || !prop.longitude) return;
    const key = `${prop.latitude.toFixed(6)},${prop.longitude.toFixed(6)}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(prop);
  });

  // For each group, offset if needed
  const result: Array<{ property: API.Property.PropertyMapData; position: google.maps.LatLngLiteral }> = [];
  Object.values(groups).forEach((group) => {
    if (group.length === 1) {
      const { latitude, longitude } = group[0];
      if (typeof latitude === "number" && typeof longitude === "number") {
        result.push({ property: group[0], position: { lat: latitude, lng: longitude } });
      }
    } else {
      // Offset in a circle
      group.forEach((prop, i) => {
        const { latitude, longitude } = prop;
        if (typeof latitude === "number" && typeof longitude === "number") {
          const angle = i * ((2 * Math.PI) / group.length);
          result.push({
            property: prop,
            position: {
              lat: latitude + Math.cos(angle) * offsetDistance,
              lng: longitude + Math.sin(angle) * offsetDistance,
            },
          });
        }
      });
    }
  });
  return result;
}

// State name to abbreviation mapping
const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

/**
 * Converts a full state name to its abbreviation
 * @param stateName - The full state name (e.g., "Alabama")
 * @returns The state abbreviation (e.g., "AL") or the original string if not found
 */
export function getStateAbbreviation(stateName: string): string {
  return STATE_ABBREVIATIONS[stateName] || stateName;
}

/**
 * Formats an address string to use state abbreviations instead of full state names
 * @param address - The address string (e.g., "Adamsville, Alabama")
 * @returns The formatted address with state abbreviation (e.g., "Adamsville, AL")
 */
export function formatAddressWithStateAbbreviation(address: string): string {
  // Split by comma to separate city and state
  const parts = address.split(",").map((part) => part.trim());

  if (parts.length >= 2) {
    const city = parts[0];
    const state = parts[1];
    const stateAbbreviation = getStateAbbreviation(state);

    // Reconstruct the address with abbreviation
    return `${city}, ${stateAbbreviation}`;
  }

  return address;
}
